import React, { useEffect, useMemo, useRef } from 'react';
import { currencySymbol } from '@/utils/currencies';
import { BarChart } from '@/features/ai-agents/chat/MarkdownTableVisualization/BarChart';
import { formatChartMetricValue } from '@/features/ai-agents/chat/MarkdownTableVisualization/helpers';
import { LineChart } from '@/features/ai-agents/chat/MarkdownTableVisualization/LineChart';
import {
  ComparePieData,
  PieChart,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/PieChart';
import { SimpleTableView } from '@/features/ai-agents/chat/MarkdownTableVisualization/SimpleTableView';
import { VisualizationControls } from '@/features/ai-agents/chat/MarkdownTableVisualization/VisualizationControls';
import {
  ChartContainer,
  Container,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import { TableView } from '@/features/ai-agents/chat/MarkdownTableVisualization/TableView';
import {
  ChartRow,
  ChartSeries,
  ChartView,
  CompareLineChartData,
  MarkdownTableVisualizationProps,
  TooltipTextResolver,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import { useChartData } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-chart-data';
import { useTableData } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-table-data';
import { useTableDragSensors } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-table-drag-sensors';
import { useColumnWidths } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-column-widths';
import { useVisualizationState } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-visualization-state';
import { useDefaultViewHint } from '@/features/ai-agents/chat/default-view-hint';
import {
  downloadCsvFile,
  formatColumnHeader,
  generateCsvFromTable,
  resolveHintedColumnWidths,
  resolveTooltipText,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

export function MarkdownTableVisualization({
  children,
  node,
  onViewChange,
}: MarkdownTableVisualizationProps) {
  const tableData = useTableData(node, children);
  const defaultViewHint = useDefaultViewHint();
  const visualization = useVisualizationState({
    chartNumericColumnIndexes: tableData.chartNumericColumnIndexes,
    compareColumnIndex: tableData.compareColumnIndex,
    defaultViewHint,
    defaultXColumnIndex: tableData.defaultXColumnIndex,
    isVariantReport: tableData.isVariantReport,
    table: tableData.table,
    variantColumnIndex: tableData.variantColumnIndex,
  });
  const { columnWidths, getColumnWidthStyle, startResizing } = useColumnWidths(
    resolveHintedColumnWidths(
      tableData.table.headers,
      defaultViewHint?.columnWidths ?? null,
    ) ?? {},
  );
  const chart = useChartData({
    breakdownColumnIndex: visualization.selectedBreakdownColumnIndex,
    compareColumnIndex: tableData.compareColumnIndex,
    isCompareReport: tableData.isCompareReport,
    isVariantReport: tableData.isVariantReport,
    selectedXColumnIndex: visualization.selectedXColumnIndex,
    selectedYColumnIndex: visualization.selectedYColumnIndex,
    sortedRows: visualization.sortedRows,
    variantColumnIndex: tableData.variantColumnIndex,
  });
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    const { headers } = tableData.table;
    onViewChange?.({
      type: visualization.activeView,
      xAxis: headers[visualization.selectedXColumnIndex],
      yAxis: headers[visualization.selectedYColumnIndex],
      breakdown: headers[visualization.selectedBreakdownColumnIndex] ?? null,
      sort: visualization.currentSort
        ? {
            column: headers[visualization.currentSort.columnIndex],
            direction: visualization.currentSort.direction,
          }
        : null,
      columnOrder: visualization.orderedColumnIndexes.map(
        (columnIndex) => headers[columnIndex],
      ),
      columnWidths: Object.fromEntries(
        Object.entries(columnWidths).map(([columnIndex, width]) => [
          headers[Number(columnIndex)],
          width,
        ]),
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visualization.activeView,
    visualization.selectedXColumnIndex,
    visualization.selectedYColumnIndex,
    visualization.selectedBreakdownColumnIndex,
    visualization.currentSort?.columnIndex,
    visualization.currentSort?.direction,
    visualization.orderedColumnIndexes.join(`,`),
    columnWidths,
  ]);
  const sensors = useTableDragSensors();
  const hintTooltips = defaultViewHint?.tooltips ?? null;
  const getTooltip: TooltipTextResolver = useMemo(
    () => (label: string) => resolveTooltipText(hintTooltips, label),
    [hintTooltips],
  );

  const handleDownloadCsv = () => {
    const csvContent = generateCsvFromTable(tableData.table);
    downloadCsvFile(csvContent, `report.csv`);
  };

  if (!visualization.canShowChart) {
    return (
      <SimpleTableView
        numericColumnIndexes={tableData.numericColumnIndexes}
        renderedTable={tableData.renderedTable}
        table={tableData.table}
      />
    );
  }

  return (
    <Container className="table-arrows-portal">
      <VisualizationControls
        activeView={visualization.activeView}
        availableBreakdownColumnIndexes={
          visualization.availableBreakdownColumnIndexes
        }
        availableChartViews={visualization.availableChartViews}
        availableXColumnIndexes={visualization.availableXColumnIndexes}
        canShowChart={visualization.canShowChart}
        chartNumericColumnIndexes={tableData.chartNumericColumnIndexes}
        getTooltip={getTooltip}
        headers={tableData.table.headers}
        onDownloadCsv={handleDownloadCsv}
        onSelectBreakdownColumnIndex={visualization.setBreakdownColumnIndex}
        onSelectView={visualization.setView}
        onSelectXColumnIndex={visualization.setXColumnIndex}
        onSelectYColumnIndex={visualization.setYColumnIndex}
        orderedColumnIndexes={visualization.orderedColumnIndexes}
        selectedBreakdownColumnIndex={
          visualization.selectedBreakdownColumnIndex
        }
        selectedXColumnIndex={visualization.selectedXColumnIndex}
        selectedYColumnIndex={visualization.selectedYColumnIndex}
      />
      {visualization.activeView === `table` ? (
        <TableView
          currentSort={visualization.currentSort}
          getColumnWidthStyle={getColumnWidthStyle}
          getTooltip={getTooltip}
          numericColumnIndexes={tableData.numericColumnIndexes}
          onColumnOrderChange={visualization.setColumnOrder}
          onSort={visualization.handleSort}
          orderedColumnIndexes={visualization.orderedColumnIndexes}
          renderedTable={tableData.renderedTable}
          sensors={sensors}
          sortedRows={visualization.sortedRows}
          startResizing={startResizing}
          table={tableData.table}
        />
      ) : (
        <DataChart
          type={visualization.activeView}
          compareGroupedData={chart.compareGroupedData}
          comparePieData={chart.comparePieData}
          data={chart.chartData}
          series={chart.series}
          valueLabel={
            tableData.table.headers[visualization.selectedYColumnIndex]
          }
        />
      )}
    </Container>
  );
}

interface DataChartProps {
  compareGroupedData?: CompareLineChartData | null;
  comparePieData?: ComparePieData | null;
  data: ChartRow[];
  series?: ChartSeries[];
  type: ChartView;
  valueLabel: string;
}

function DataChart({
  compareGroupedData,
  comparePieData,
  data,
  series = [],
  type,
  valueLabel,
}: DataChartProps) {
  const symbol = currencySymbol();
  const formatValue = (value: number | string) =>
    formatChartMetricValue(value, valueLabel, symbol);

  return (
    <ChartContainer role="img" aria-label={`${type} chart`}>
      {type === `line` && (
        <LineChart
          compareData={compareGroupedData}
          data={data}
          formatValue={formatValue}
          series={series}
          valueLabel={valueLabel}
        />
      )}
      {type === `bar` && (
        <BarChart
          compareData={compareGroupedData}
          data={data}
          formatValue={formatValue}
          series={series}
          valueLabel={valueLabel}
        />
      )}
      {type === `pie` && (
        <PieChart
          compareData={comparePieData}
          data={data}
          formatValue={formatValue}
          totalLabel={`Total ${formatColumnHeader(valueLabel)}`}
          valueLabel={valueLabel}
        />
      )}
    </ChartContainer>
  );
}

export type { DefaultViewChange } from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
export {
  formatChartMetricValue,
  isCurrencyMetric,
  isPercentageMetric,
  PieValueLabel,
  TruncatedXAxisTick,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/helpers';
export {
  MarkdownTableBody,
  MarkdownTableCell,
  MarkdownTableHead,
  MarkdownTableHeader,
  MarkdownTableRow,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/TableView';
export {
  createVariantChartSeries,
  createVariantLineChartData,
  downloadCsvFile,
  escapeCsvCell,
  findVariantColumnIndex,
  formatColumnHeader,
  generateCsvFromTable,
  parseMarkdownTable,
  parseNumericValue,
  reorderColumns,
  resolveTooltipText,
  sortChartRowsByDate,
  sortTableRows,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';
